/**
 * Modal form for adding custom stat cards to reports
 * 
 * Features:
 * - Dynamic column filtering based on datasource
 * - Smart dimension validation based on aggregation type
 * - Real-time field type detection from datasource config
 * - Reusable for custom report wizard
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Stack,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Collapse,
  Chip,
  IconButton,
  useTheme,
} from "@mui/material";
import { ExpandMore, ExpandLess, Settings, AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";

import { useState, useEffect, useMemo, useCallback } from "react";
import type { ConfigDrivenReportConfig, StatCardConfig } from "@hooks/use-reports/types";
import {
  getDimensionRequirements,
  filterColumnsForDimension,
  validateDimensions,
  type ColumnMetadata,
} from "./utils";
import { useReportFieldOptions } from "../report-config/use-report-field-options";
import { useGetAggregateAssets } from "@hooks/use-get/aggregate-assets";
import { getDistinctAggregateValues } from "@hooks/use-get/aggregate-assets/utils";
import {
  AVAILABLE_ICONS,
  PERIOD_OPTIONS,
  PERIOD_LABEL_MAP,
  EMPTY_STAT_FORM_STATE,
  type StatCardFormState,
} from "../reporting-constants";
import { useReportFormModal } from "@hooks/use-report-form-modal";

// ─────────────────────────────────────────────────────────────────────────────
// FilterConditionRow
// Self-contained row that loads its own column options so the parent modal
// stays free of per-row state.
// ─────────────────────────────────────────────────────────────────────────────
interface FilterConditionRowProps {
  condition: { column: string; values: string[] };
  filterableColumns: ColumnMetadata[];
  availableColumns: ColumnMetadata[];
  onColumnChange: (col: string) => void;
  onValuesChange: (vals: string[]) => void;
  onRemove: () => void;
  canRemove: boolean;
}

const FilterConditionRow = ({
  condition,
  filterableColumns,
  availableColumns,
  onColumnChange,
  onValuesChange,
  onRemove,
  canRemove,
}: FilterConditionRowProps) => {
  const { getAggregateAssets } = useGetAggregateAssets();
  const [options, setOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!condition.column) {
      setOptions([]);
      return;
    }
    const col = availableColumns.find((c) => c.id === condition.column);
    if (!col) {
      setOptions([]);
      return;
    }
    if (col.hasOptions) {
      setOptions(
        (col.options ?? []).map((o) => ({ label: String(o.label), value: String(o.value) }))
      );
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAggregateAssets({ groupBy: [condition.column] })
      .then((result: { data: Record<string, string | number>[] }) => {
        if (cancelled) return;
        const vals = getDistinctAggregateValues(result.data, condition.column);
        setOptions(vals.map((v) => ({ label: v, value: v })));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [condition.column, availableColumns, getAggregateAssets]);

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
      <FormControl size="small" sx={{ flex: 1 }}>
        <InputLabel>Column</InputLabel>
        <Select
          value={condition.column}
          label="Column"
          onChange={(e) => onColumnChange(e.target.value)}
        >
          {filterableColumns.map((col) => (
            <MenuItem key={col.id} value={col.id}>
              {col.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {condition.column && loading && (
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>Values</InputLabel>
          <Select multiple value={[]} label="Values">
            <MenuItem disabled>Loading…</MenuItem>
          </Select>
        </FormControl>
      )}

      {condition.column && !loading && options.length > 0 && (
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>Values</InputLabel>
          <Select
            multiple
            value={condition.values}
            label="Values"
            onChange={(e) => {
              const val = e.target.value;
              onValuesChange(typeof val === "string" ? val.split(",") : (val as string[]));
            }}
          >
            {options.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Click to select one or more values (OR logic)</FormHelperText>
        </FormControl>
      )}

      {condition.column && !loading && options.length === 0 && (
        <TextField
          size="small"
          label="Values"
          placeholder="Enter value or multiple values separated by comma"
          value={condition.values.join(", ")}
          onChange={(e) => {
            const vals = e.target.value
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean);
            onValuesChange(vals);
          }}
          helperText='Comma-separated, exact match (case-sensitive). Use "null" to match blank fields.'
          sx={{ flex: 1 }}
        />
      )}

      <IconButton
        size="small"
        onClick={onRemove}
        disabled={!canRemove}
        sx={{ mt: 0.5, color: "text.secondary" }}
      >
        <RemoveCircleOutline fontSize="small" />
      </IconButton>
    </Box>
  );
};

export interface AddStatCardModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (statCardConfig: StatCardConfig) => void;
  reportConfig: ConfigDrivenReportConfig;
  editingCardId?: string | null;
  editingConfig?: any;
  onUpdate?: (cardId: string, statCardConfig: StatCardConfig) => void;
}

export const AddStatCardModal = ({
  open,
  onClose,
  onConfirm,
  reportConfig,
  editingCardId = null,
  editingConfig = null,
  onUpdate = () => {},
}: AddStatCardModalProps) => {
  const theme = useTheme();

  // Color palette mapping for display
  const COLOR_PALETTE = useMemo(() => ({
    primary: { label: "Primary", color: theme.palette.primary.main },
    secondary: { label: "Secondary", color: theme.palette.secondary.main },
    info: { label: "Info", color: theme.palette.info.main },
    success: { label: "Success", color: theme.palette.success.main },
    warning: { label: "Warning", color: theme.palette.warning.main },
    error: { label: "Error", color: theme.palette.error.main },
  }), [theme.palette]);

  // Form state management using generic hook
  const { formState, setFormState, formErrors, setFormErrors, updateField, updateDimension, resetForm, populateFromConfig } = useReportFormModal<StatCardFormState>({
    emptyState: EMPTY_STAT_FORM_STATE,
    onValidate: (state) => {
      const errors: Record<string, string> = {};

      if (!state.title.trim()) {
        errors.title = "Title is required";
      }

      if (!state.aggregation) {
        errors.aggregation = "Aggregation type is required";
      }

      // Validate dimensions - need availableColumns to check
      // This will be handled by handleAggregationChange inline validation instead
      return { isValid: Object.keys(errors).length === 0, errors };
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBasicConfig, setShowBasicConfig] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showLayoutOptions, setShowLayoutOptions] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const {
    availableColumns,
    availableDateColumns,
    filterableColumns,
  } = useReportFieldOptions({
    reportConfig,
    filterColumn: "",
    useFilter: false,
  });

  // Get dimension requirements based on aggregation type
  const dimensionRequirements = useMemo(
    () => getDimensionRequirements(formState.aggregation),
    [formState.aggregation]
  );

  // Whether any real numeric columns exist (beyond the synthetic "count" column)
  const hasRealNumericColumns = useMemo(
    () => availableColumns.some((col) => col.isNumeric),
    [availableColumns]
  );

  // Get available columns for each dimension
  const dimensionOptionsPerIndex = useMemo(() => {
    return formState.dimensions.map((_, idx) =>
      filterColumnsForDimension(availableColumns, formState.aggregation, idx)
    );
  }, [availableColumns, formState.aggregation, formState.dimensions]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      if (editingConfig) {
        // Pre-populate form with editing config
        const isTotalItems =
          editingConfig.aggregation === "sum" &&
          editingConfig.dimensions?.[0] === "count";
        populateFromConfig({
          title: editingConfig.title || "",
          aggregation: isTotalItems ? "total-items" : (editingConfig.aggregation || "count"),
          dimensions: isTotalItems ? [] : (editingConfig.dimensions || [null]),
          icon: editingConfig.icon || "InfoIcon",
          color: editingConfig.color || "primary",
          prefix: editingConfig.prefix || "",
          suffix: editingConfig.suffix || "",
          tooltip: editingConfig.tooltip || "",
          filterConditions: editingConfig.filters
            ? editingConfig.filters.map((f: any) => ({ column: f.column, values: [...f.values] }))
            : editingConfig.filter
              ? [{ column: editingConfig.filter.column, values: [...editingConfig.filter.values] }]
              : [],
          period: editingConfig.period || "",
          periodColumn: editingConfig.periodColumn || "",
          usePeriod: Boolean(editingConfig.period),
        });
      } else {
        resetForm();
      }
      setShowAdvanced(false);
      setShowLayoutOptions(false);
      setShowFilters(Boolean(editingConfig?.filters?.length || editingConfig?.filter));
    }
  }, [open, editingConfig, populateFromConfig, resetForm]);

  // Handle aggregation type change - reset dimensions based on requirements
  const handleAggregationChange = useCallback((newAggregation: string) => {
    updateField("aggregation", newAggregation as "sum" | "average" | "count" | "total-items");
    const newRequirements = getDimensionRequirements(newAggregation);
    setFormState((prev) => ({
      ...prev,
      dimensions: new Array(newRequirements.count).fill(null),
    }));
  }, [updateField, setFormState]);

  // Validate form
  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!formState.title.trim()) {
      errors.title = "Title is required";
    }

    if (!formState.aggregation) {
      errors.aggregation = "Aggregation type is required";
    }

    // Validate dimensions
    const dimensionValidation = validateDimensions(
      formState.dimensions,
      availableColumns,
      formState.aggregation
    );
    if (!dimensionValidation.isValid) {
      errors.dimensions = dimensionValidation.errorMessage || "Invalid dimension selection";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formState, availableColumns, setFormErrors]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Build StatCardConfig from form state
      const isTotalItems = formState.aggregation === "total-items";
      const configBase: StatCardConfig = {
        id: "", // Will be set by useReportLayout (or kept for edit)
        title: formState.title.trim(),
        aggregation: isTotalItems ? "sum" : (formState.aggregation as "sum" | "average" | "count"),
        dimensions: isTotalItems ? ["count"] : formState.dimensions.filter((d) => d !== null && d !== undefined) as string[],
        icon: formState.icon || "InfoIcon",
        color: formState.color,
        prefix: formState.prefix || undefined,
        suffix: formState.suffix || undefined,
        tooltip: formState.tooltip || undefined,
        ...(formState.period ? {
          period: formState.period as StatCardConfig["period"],
          ...(formState.periodColumn ? { periodColumn: formState.periodColumn } : {}),
        } : {}),
      };

      // Add filters if specified (AND-combined conditions)
      const activeFilters = formState.filterConditions.filter(
        (c) => c.column && c.values.length > 0
      );
      const config: StatCardConfig = activeFilters.length > 0
        ? { ...configBase, filters: activeFilters }
        : configBase;

      // Call appropriate handler based on mode
      if (editingCardId) {
        onUpdate(editingCardId, config);
      } else {
        onConfirm(config);
      }

      // Reset form
      setFormState(EMPTY_STAT_FORM_STATE);
      setFormErrors({});
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  }, [formState, validateForm, onConfirm, onUpdate, editingCardId, onClose, setFormState, setFormErrors]);

  // Is form valid (for submit button)
  const isFormValid =
    formState.title.trim() &&
    formState.dimensions.filter((d) => d !== null).length === dimensionRequirements.count &&
    validateDimensions(formState.dimensions, availableColumns, formState.aggregation).isValid;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editingCardId ? "Edit Custom Stat Card" : "Add Custom Stat Card"}</DialogTitle>

      <DialogContent sx={{ minHeight: "500px", overflow: "auto", pt: 2 }}>
        <Stack spacing={2}>
          {/* Validation Alert */}
          {Object.keys(formErrors).length > 0 && (
            <Alert severity="error">
              {Object.values(formErrors)[0]}
            </Alert>
          )}

          {/* Basic Configuration */}
          <Box>
            <Button
              onClick={() => setShowBasicConfig(!showBasicConfig)}
              size="small"
              sx={{ p: 0, mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {showBasicConfig ? <ExpandLess /> : <ExpandMore />}
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Basic Configuration
              </Typography>
            </Button>

            <Collapse in={showBasicConfig} timeout="auto" unmountOnExit>
              <Stack spacing={2}>
                {/* Title */}
                <TextField
                  label="Title"
                  placeholder="e.g., 'Total Revenue', 'Assets by Site'"
                  value={formState.title}
                  onChange={(e) => updateField("title", e.target.value)}
                  fullWidth
                  size="small"
                  error={Boolean(formErrors.title)}
                  helperText={formErrors.title || undefined}
                />

                {/* Icon and Color Selection (Side by Side) */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  {/* Color Selection */}
                  <FormControl fullWidth size="small" sx={{ flex: 1 }}>
                    <InputLabel id="color-label">Color</InputLabel>
                    <Select
                      labelId="color-label"
                      value={formState.color || "primary"}
                      label="Color"
                      onChange={(e) => updateField("color", e.target.value)}
                      renderValue={(value) => {
                        const colorData = COLOR_PALETTE[value as keyof typeof COLOR_PALETTE];
                        if (!colorData) return value;
                        return (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "4px",
                                backgroundColor: colorData.color,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            />
                            <span>{colorData.label}</span>
                          </Box>
                        );
                      }}
                    >
                      {Object.entries(COLOR_PALETTE).map(([key, { label, color }]) => (
                        <MenuItem key={key} value={key}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "4px",
                                backgroundColor: color,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            />
                            <span>{label}</span>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Icon Selection */}
                  <FormControl fullWidth size="small" sx={{ flex: 1 }}>
                    <InputLabel id="icon-label">Icon</InputLabel>
                    <Select
                      labelId="icon-label"
                      value={formState.icon}
                      label="Icon"
                      onChange={(e) => updateField("icon", e.target.value)}
                      renderValue={(value) => {
                        const iconData = AVAILABLE_ICONS.find((i) => i.id === value);
                        if (!iconData) return value;
                        const IconComponent = iconData.component;
                        return (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <IconComponent sx={{ fontSize: 20 }} />
                            <span>{iconData.label}</span>
                          </Box>
                        );
                      }}
                    >
                      {AVAILABLE_ICONS.map((iconData) => {
                        const IconComponent = iconData.component;
                        return (
                          <MenuItem key={iconData.id} value={iconData.id}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <IconComponent sx={{ fontSize: 20 }} />
                              <span>{iconData.label}</span>
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>

                {/* Aggregation Type + Dimensions */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  {/* Aggregation Type */}
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel id="aggregation-label">Aggregation Type</InputLabel>
                    <Select
                      labelId="aggregation-label"
                      value={formState.aggregation}
                      label="Aggregation Type"
                      onChange={(e) => handleAggregationChange(e.target.value)}
                    >
                      <MenuItem value="total-items">All Items</MenuItem>
                      <MenuItem value="count">Count</MenuItem>
                      {hasRealNumericColumns && <MenuItem value="sum">Sum</MenuItem>}
                      <MenuItem value="average">Average</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Dimension Selection */}
                  <Box sx={{ flex: 1, pl: 1, borderLeft: "3px solid", borderLeftColor: "divider" }}>

                    {formState.dimensions.map((dimValue, idx) => (
                      <FormControl key={idx} fullWidth size="small" sx={{ mb: idx < formState.dimensions.length - 1 ? 1 : 0 }}>
                        <InputLabel id={`dimension-${idx}-label`}>
                          {dimensionRequirements.labels[idx] || `Dimension ${idx + 1}`}
                        </InputLabel>
                        <Select
                          labelId={`dimension-${idx}-label`}
                          value={dimValue || ""}
                          label={dimensionRequirements.labels[idx] || `Dimension ${idx + 1}`}
                          onChange={(e) => updateDimension(idx, e.target.value || null)}
                          error={Boolean(formErrors.dimensions)}
                        >
                          {dimensionOptionsPerIndex[idx]?.map((col) => (
                            <MenuItem key={col.id} value={col.id}>
                              {col.label}
                            </MenuItem>
                          ))}
                        </Select>


                      </FormControl>
                    ))}

                    {formErrors.dimensions && (
                      <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                        {formErrors.dimensions}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Stack>
            </Collapse>
          </Box>

          {/* Optional (but powerful) Configurations */}
          <Box>
            <Button
              onClick={() => setShowAdvanced(!showAdvanced)}
              size="small"
              sx={{ p: 0, display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
            >
              {showAdvanced ? <ExpandLess /> : <ExpandMore />}
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Optional (but powerful) Configurations
              </Typography>
            </Button>

            {/* Display chips for applied optional configs OUTSIDE the collapse */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: showAdvanced ? 2 : 0 }}>
              {formState.filterConditions.some((c) => c.column && c.values.length > 0) && (
                <Chip
                  size="small"
                  label={`Filters: ${formState.filterConditions.filter((c) => c.column && c.values.length > 0).length} condition(s)`}
                  onDelete={() => updateField("filterConditions", [])}
                  icon={<Settings />}
                  onClick={() => { setShowAdvanced(true); setShowFilters(true); }}
                  variant="outlined"
                />
              )}
              {formState.period && (
                <Chip
                  size="small"
                  label={`Timeframe: ${PERIOD_LABEL_MAP[formState.period]}`}
                  onDelete={() => {
                    updateField("period", "");
                    updateField("periodColumn", "");
                  }}
                  icon={<Settings />}
                  onClick={() => updateField("usePeriod", true)}
                  variant="outlined"
                />
              )}
              {formState.prefix && (
                <Chip
                  size="small"
                  label={`Prefix: ${formState.prefix}`}
                  onDelete={() => updateField("prefix", "")}
                  icon={<Settings />}
                  onClick={() => setShowLayoutOptions(true)}
                  variant="outlined"
                />
              )}
              {formState.suffix && (
                <Chip
                  size="small"
                  label={`Suffix: ${formState.suffix}`}
                  onDelete={() => updateField("suffix", "")}
                  icon={<Settings />}
                  onClick={() => setShowLayoutOptions(true)}
                  variant="outlined"
                />
              )}
              {formState.tooltip && (
                <Chip
                  size="small"
                  label={`Tooltip: ${formState.tooltip.substring(0, 20)}${formState.tooltip.length > 20 ? "..." : ""}`}
                  onDelete={() => updateField("tooltip", "")}
                  icon={<Settings />}
                  onClick={() => setShowLayoutOptions(true)}
                  variant="outlined"
                />
              )}
            </Box>

            <Collapse in={showAdvanced} timeout="auto" unmountOnExit>
              <Stack spacing={1.5}>

                {/* Filter Conditions */}
                <Box>
                  <Button
                    size="small"
                    onClick={() => {
                      if (!showFilters && formState.filterConditions.length === 0) {
                        updateField("filterConditions", [{ column: "", values: [] }]);
                      }
                      setShowFilters((v) => !v);
                    }}
                    sx={{ p: 0, display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    {showFilters ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                      Filters
                    </Typography>
                  </Button>

                  <Collapse in={showFilters} timeout="auto" unmountOnExit>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      {formState.filterConditions.map((condition, idx) => (
                        <FilterConditionRow
                          key={idx}
                          condition={condition}
                          filterableColumns={filterableColumns}
                          availableColumns={availableColumns}
                          onColumnChange={(col) => {
                            const next = [...formState.filterConditions];
                            next[idx] = { column: col, values: [] };
                            updateField("filterConditions", next);
                          }}
                          onValuesChange={(vals) => {
                            const next = [...formState.filterConditions];
                            next[idx] = { ...next[idx], values: vals };
                            updateField("filterConditions", next);
                          }}
                          onRemove={() => {
                            const next = formState.filterConditions.filter((_, i) => i !== idx);
                            updateField("filterConditions", next);
                            if (next.length === 0) setShowFilters(false);
                          }}
                          canRemove={formState.filterConditions.length > 1}
                        />
                      ))}
                      <Button
                        size="small"
                        startIcon={<AddCircleOutline fontSize="small" />}
                        onClick={() =>
                          updateField("filterConditions", [
                            ...formState.filterConditions,
                            { column: "", values: [] },
                          ])
                        }
                        sx={{ alignSelf: "flex-start", pl: 0 }}
                      >
                        Add condition
                      </Button>
                    </Stack>
                  </Collapse>
                </Box>

                {/* Timeframe */}
                <Box>
                  <Button
                    onClick={() => updateField("usePeriod", !formState.usePeriod)}
                    size="small"
                    sx={{ p: 0, display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    {formState.usePeriod ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                      Timeframe
                    </Typography>
                  </Button>

                  <Collapse in={formState.usePeriod} timeout="auto" unmountOnExit>
                    {formState.period ? (
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={`${availableDateColumns.find((c) => c.id === formState.periodColumn)?.label ?? formState.periodColumn}: ${PERIOD_LABEL_MAP[formState.period]}`}
                          onDelete={() => {
                            updateField("period", "");
                            updateField("periodColumn", "");
                          }}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                        <FormControl fullWidth size="small" sx={{ flex: 1 }}>
                          <InputLabel id="period-column-label">Date Column</InputLabel>
                          <Select
                            labelId="period-column-label"
                            value={formState.periodColumn}
                            label="Date Column"
                            onChange={(e) => updateField("periodColumn", e.target.value)}
                          >
                            {availableDateColumns.map((col) => (
                              <MenuItem key={col.id} value={col.id}>
                                {col.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {formState.periodColumn && (
                          <FormControl fullWidth size="small" sx={{ flex: 1 }}>
                            <InputLabel id="period-label">Period</InputLabel>
                            <Select
                              labelId="period-label"
                              value={formState.period}
                              label="Period"
                              onChange={(e) => updateField("period", e.target.value as StatCardFormState["period"])}
                            >
                              {PERIOD_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </Box>
                    )}
                  </Collapse>
                </Box>

                {/* Layout Options */}
                <Box>
                  <Button
                    onClick={() => setShowLayoutOptions(!showLayoutOptions)}
                    size="small"
                    sx={{ p: 0, display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    {showLayoutOptions ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                      Layout Options
                    </Typography>
                  </Button>

                  <Collapse in={showLayoutOptions} timeout="auto" unmountOnExit>
                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                      {/* Prefix */}
                      <TextField
                        label="Prefix"
                        placeholder="e.g., $, £, ~"
                        value={formState.prefix}
                        onChange={(e) => updateField("prefix", e.target.value)}
                        fullWidth
                        size="small"
                      />

                      {/* Suffix */}
                      <TextField
                        label="Suffix"
                        placeholder="e.g., %, months, items"
                        value={formState.suffix}
                        onChange={(e) => updateField("suffix", e.target.value)}
                        fullWidth
                        size="small"
                      />

                      {/* Tooltip */}
                      <TextField
                        label="Tooltip"
                        placeholder="Hover text to explain this metric"
                        value={formState.tooltip}
                        onChange={(e) => updateField("tooltip", e.target.value)}
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                      />
                    </Stack>
                  </Collapse>
                </Box>

              </Stack>
            </Collapse>
          </Box>

        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!isFormValid || isSubmitting}
          sx={{ position: "relative" }}
        >
          {isSubmitting && (
            <CircularProgress
              size={24}
              sx={{ position: "absolute", left: "50%", marginLeft: "-12px" }}
            />
          )}
          <span style={{ visibility: isSubmitting ? "hidden" : "visible" }}>
            {editingCardId ? "Update Stat Card" : "Add Stat Card"}
          </span>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

